import { slotNeedsRemoteSpeakerConfirmation } from '@/utils/remote-speaker-status.utils';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { ContributorRole } from '@/types/dtos/contributors/response/contributor.dto';
import type {
  ContributorRoleSlotSummaryDto,
  ContributorRoleSummaryDto,
  ContributorSummaryDto,
} from '@/types/dtos/contributors/response/contributor-summary.dto';
import {
  CheckCircleOutline,
  EventAvailableOutlined,
  FiberManualRecord,
  FileUploadOutlined,
  GroupsOutlined,
  HelpOutline,
  InfoOutlined,
  MicOutlined,
  PendingActionsOutlined,
  PersonOutline,
  PhoneCallbackOutlined,
  RecordVoiceOverOutlined,
  StarOutline,
} from '@mui/icons-material';
import type { SessionSummaryDto } from '@/types/dtos/sessions/response/session-summary.dto';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  mergeRoleMedia,
  resolveContributorMediaProcessingIssue,
  roleMediaNeedsContributorUploadAction,
} from '@/components/user-dashboard/contributions/contributor-role-media.utils.ts';
import { MediaProcessingIssueIcon } from '@/components/user-dashboard/contributions/media-processing-issue-icon';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import type { FlowSummaryDto, FlowSlotSummaryDto } from '@/types/dtos/flows/response/flow-summary.dto';
import RoleDetailsModal from './role-details.modal';
import { JoinLiveButton } from './join-live-button';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE } from '@/constants/feature-flags';

type ContributionsListMode = 'widget' | 'details';

const ClickableIconTooltip = ({
  title,
  icon,
  color = 'text.secondary',
}: {
  title: string;
  icon: ReactNode;
  color?: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
        <Tooltip
          title={title}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disableTouchListener
          arrow
          slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
        >
          <IconButton
            size="small"
            type="button"
            aria-label={title}
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
            sx={{ p: 0.25, color }}
          >
            {icon}
          </IconButton>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

const ContributionsList = ({
  contributor,
  event,
  flows,
  sessions,
  mode = 'widget',
  onRoleSelectionChange,
  onSlotSelectionChange,
  selectedRoleIds,
  selectedSlotIds,
}: {
  contributor: ContributorSummaryDto;
  event?: EventSummaryDto;
  flows: FlowSummaryDto[];
  sessions: SessionSummaryDto[];
  mode?: ContributionsListMode;
  onRoleSelectionChange?: (roleId: string, selected: boolean) => void;
  onSlotSelectionChange?: (roleId: string, slotId: string, selected: boolean) => void;
  selectedRoleIds?: Set<string>;
  selectedSlotIds?: Map<string, Set<string>>;
}) => {
  const flowsById = useMemo(() => new Map(flows.map((flow) => [flow.id, flow])), [flows]);
  const sessionsById = useMemo(() => new Map(sessions.map((session) => [session.id, session])), [sessions]);
  const t = useTranslations();
  const { hasAccess: hasRemoteSpeakershipAccess, isLoading: isRemoteSpeakershipAccessLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const remoteSpeakershipAvailable = !isRemoteSpeakershipAccessLoading && hasRemoteSpeakershipAccess;
  const [selectedRole, setSelectedRole] = useState<ContributorRoleSummaryDto | null>(null);

  const getRoleEntityTitle = useCallback(
    (role: ContributorRoleSummaryDto) => {
      if (role.entityType === 'Event') {
        return event?.title || t('myEventsList.unknown');
      }
      if (!role.entityId) return t('myEventsList.unknown');
      if (role.entityType === 'Session') {
        return sessionsById.get(role.entityId)?.title || t('myEventsList.unknown');
      }
      if (role.entityType === 'Flow') {
        return flowsById.get(role.entityId)?.title || t('myEventsList.unknown');
      }
      return t('myEventsList.unknown');
    },
    [event?.title, flowsById, sessionsById, t],
  );

  const getRoleMedia = useCallback(
    (role: ContributorRoleSummaryDto) => {
      if (role.entityType === 'Event') return [];
      if (!role.entityId) return [];
      if (role.entityType === 'Session') {
        return sessionsById.get(role.entityId)?.media ?? [];
      }
      if (role.entityType === 'Flow') {
        return flowsById.get(role.entityId)?.media ?? [];
      }
      return [];
    },
    [flowsById, sessionsById],
  );

  const roleNeedsMediaUploadAction = useCallback(
    (role: ContributorRoleSummaryDto) => {
      const roleMedia = mergeRoleMedia({
        role,
        contributorMedia: contributor.media ?? [],
        entityMedia: getRoleMedia(role),
        eventId: event?.id,
      });
      return roleMediaNeedsContributorUploadAction(roleMedia).needsUploadAction;
    },
    [getRoleMedia, contributor.media, event?.id],
  );

  const getRoleMediaProcessingIssue = useCallback(
    (role: ContributorRoleSummaryDto) => {
      const roleMedia = mergeRoleMedia({
        role,
        contributorMedia: contributor.media ?? [],
        entityMedia: getRoleMedia(role),
        eventId: event?.id,
      });
      return resolveContributorMediaProcessingIssue(roleMedia);
    },
    [getRoleMedia, contributor.media, event?.id],
  );

  const roleNeedsAction = useCallback(
    (role: ContributorRoleSummaryDto) => {
      const hasMissingRoleAcceptance = !role.acceptedAt;
      const hasMissingSlotAcceptance =
        role.slots?.some(
          (slot) => !slot.acceptedAt || (remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot)),
        ) ?? false;
      const hasMissingMedia = role.acceptedAt ? roleNeedsMediaUploadAction(role) : false;
      const hasProcessingIssue = role.acceptedAt ? Boolean(getRoleMediaProcessingIssue(role)) : false;
      return hasMissingRoleAcceptance || hasMissingSlotAcceptance || hasMissingMedia || hasProcessingIssue;
    },
    [getRoleMediaProcessingIssue, remoteSpeakershipAvailable, roleNeedsMediaUploadAction],
  );

  const getRoleActionDotColor = useCallback(
    (role: ContributorRoleSummaryDto): 'primary' | 'warning' | 'error' => {
      const processingIssue = role.acceptedAt ? getRoleMediaProcessingIssue(role) : undefined;
      if (processingIssue === 'error') return 'error';
      if (processingIssue === 'warning') return 'warning';
      return 'primary';
    },
    [getRoleMediaProcessingIssue],
  );

  const getFlowSlotTimingSummary = (
    currentEvent: EventSummaryDto,
    flowSlotInEvent: EventSlotDto,
    contentSlotInFlow: FlowSlotSummaryDto,
  ) => {
    if (!flowSlotInEvent.day || !currentEvent.days) {
      return 'Timing unavailable';
    }

    const day = currentEvent.days.find((day) => day.id === flowSlotInEvent.day.id);
    if (!day) {
      return 'Timing unavailable';
    }

    const dayNumber = currentEvent.days.indexOf(day) + 1;
    if (!currentEvent.startDate) {
      return `Day ${dayNumber}`;
    }

    const slotOffsetMinutes =
      contentSlotInFlow?.computedOffsetMinutes ?? contentSlotInFlow?.plannedOffsetMinutes ?? null;

    if (slotOffsetMinutes === null) {
      return `Day ${dayNumber}`;
    }

    const startingOffsetInDay = flowSlotInEvent.startOffsetMinutes;
    const slotStartOffsetFromEventStart = startingOffsetInDay + slotOffsetMinutes;
    const slotStartTime = minuteOffsetToTime(slotStartOffsetFromEventStart, currentEvent.startDate);

    const slotDuration = contentSlotInFlow?.computedDurationMinutes ?? contentSlotInFlow?.plannedDurationMinutes;
    if (slotDuration === undefined) {
      return `Day ${dayNumber} | ${slotStartTime}`;
    }

    const slotEndOffsetFromEventStart = slotStartOffsetFromEventStart + slotDuration;
    const slotEndTime = minuteOffsetToTime(slotEndOffsetFromEventStart, currentEvent.startDate);

    const timeRange = `${slotStartTime} - ${slotEndTime}`;
    return `Day ${dayNumber} | ${timeRange}`;
  };

  const getFlowDayAndTimeInfoSummary = (
    flow: FlowSummaryDto,
    currentEvent: EventSummaryDto,
    eventSlot?: EventSlotDto,
  ) => {
    if (!currentEvent.slots || !currentEvent.days || !currentEvent.startDate) {
      return '';
    }

    const flowSlotInEvent =
      eventSlot || currentEvent.slots.find((slot) => slot.content === flow.id || slot.origin === flow.id);

    if (!flowSlotInEvent) {
      return '';
    }

    const day = currentEvent.days.find((day) => day.id === flowSlotInEvent.day.id);
    if (!day) {
      return '';
    }

    const dayNumber = currentEvent.days.indexOf(day) + 1;
    const startOffsetMinutes = flowSlotInEvent.startOffsetMinutes;
    const slotStartTime = minuteOffsetToTime(startOffsetMinutes, currentEvent.startDate);

    const durationMinutes = flowSlotInEvent.durationPlannedMinutes;
    if (durationMinutes === undefined) {
      return `Day ${dayNumber} | ${slotStartTime}`;
    }

    const endOffsetMinutes = startOffsetMinutes + durationMinutes;
    const slotEndTime = minuteOffsetToTime(endOffsetMinutes, currentEvent.startDate);
    return `Day ${dayNumber} | ${slotStartTime} - ${slotEndTime}`;
  };

  const isEventSlotEntity = (entity: EventSlotDto | undefined): entity is EventSlotDto => {
    return !!entity && typeof entity.startOffsetMinutes === 'number' && !!entity.day;
  };

  const isFlowSlotEntity = (entity: FlowSlotSummaryDto | undefined): entity is FlowSlotSummaryDto => {
    return (
      !!entity &&
      typeof entity === 'object' &&
      'contentType' in entity &&
      ('plannedOffsetMinutes' in entity || 'computedOffsetMinutes' in entity)
    );
  };

  const findEventSlot = useCallback(
    (eventSlotId: string | undefined) => {
      if (!event || !eventSlotId) return undefined;
      return event.slots?.find((slot) => slot.id === eventSlotId || slot.origin === eventSlotId);
    },
    [event],
  );

  const getRoleSlotTimingText = useCallback(
    (roleSlot: ContributorRoleSlotSummaryDto) => {
      if (!event) return '';
      if (!event.slots || !event.days) return '';

      if (roleSlot.entityType === 'FlowSlot') {
        const entityId = roleSlot.entityId;
        if (!entityId) return '';
        const flowId = roleSlot.parentEntity;
        const flow = flowId ? flowsById.get(flowId) : undefined;
        const contentSlotInFlow = flow?.slots?.find((slot) => slot.id === entityId || slot.origin === entityId);
        const flowSlotInEvent =
          findEventSlot(roleSlot.eventSlotId) ||
          (flowId ? event.slots.find((slot) => slot.content === flowId || slot.origin === flowId) : undefined) ||
          event.slots.find((slot) => slot.content === entityId || slot.origin === entityId);

        if (flowSlotInEvent && contentSlotInFlow && isFlowSlotEntity(contentSlotInFlow)) {
          return getFlowSlotTimingSummary(event, flowSlotInEvent, contentSlotInFlow);
        }

        return '';
      }

      if (roleSlot.entityType === 'EventSlot') {
        const eventSlot = findEventSlot(roleSlot.entityId) || findEventSlot(roleSlot.eventSlotId);
        if (!eventSlot || !isEventSlotEntity(eventSlot)) return '';
        const dayIndex = event.days.findIndex((day) => day.id === eventSlot.day.id);
        if (dayIndex === -1) return '';
        const dayNumber = dayIndex + 1;
        const startTime = minuteOffsetToTime(eventSlot.startOffsetMinutes, event.startDate);
        const durationMinutes = eventSlot.durationPlannedMinutes;
        if (!durationMinutes) {
          return `Day ${dayNumber} | ${startTime}`;
        }
        const endTime = minuteOffsetToTime(eventSlot.startOffsetMinutes + durationMinutes, event.startDate);
        return `Day ${dayNumber} | ${startTime} - ${endTime}`;
      }

      if (roleSlot.entityType === 'Flow') {
        const flowId = roleSlot.entityId;
        if (!flowId) return '';
        const flow = flowsById.get(flowId);
        if (!flow) return '';
        const flowSlotInEvent =
          findEventSlot(roleSlot.eventSlotId) ||
          event.slots.find((slot) => slot.content === flowId || slot.origin === flowId);
        return getFlowDayAndTimeInfoSummary(flow, event, flowSlotInEvent);
      }

      return '';
    },
    [event, findEventSlot, flowsById],
  );

  const getRoleSlotTitle = useCallback(
    (roleSlot: ContributorRoleSlotSummaryDto) => {
      if (roleSlot.entityType !== 'FlowSlot') return undefined;
      const entityId = roleSlot.entityId;
      if (!entityId) return undefined;
      const flowId = roleSlot.parentEntity;
      const flow = flowId ? flowsById.get(flowId) : undefined;
      if (flow?.title) return flow.title;
      const contentSlotInFlow = flow?.slots?.find((slot) => slot.id === entityId || slot.origin === entityId);
      if (!contentSlotInFlow) return undefined;
      if (contentSlotInFlow.title) return contentSlotInFlow.title;
      const content = contentSlotInFlow.content;
      if (typeof content === 'string') {
        return sessionsById.get(content)?.title;
      }
      return content?.title;
    },
    [flowsById, sessionsById],
  );

  const getRoleTypeIcon = useCallback((role: ContributorRole) => {
    switch (role) {
      case 'owner':
        return <StarOutline />;
      case 'event_manager':
        return <EventAvailableOutlined />;
      case 'moderator':
        return <MicOutlined />;
      case 'speaker':
        return <RecordVoiceOverOutlined />;
      case 'panelist':
        return <GroupsOutlined />;
      case 'panel_host':
        return <PersonOutline />;
      default:
        return <HelpOutline />;
    }
  }, []);

  const handleOpenRoleModal = useCallback((role: ContributorRoleSummaryDto) => {
    setSelectedRole(role);
  }, []);

  const handleCloseRoleModal = useCallback(() => {
    setSelectedRole(null);
  }, []);

  const roleSlotsByRoleId = useMemo(() => {
    const map = new Map<string, ContributorRoleSlotSummaryDto[]>();
    contributor.roles.forEach((role) => {
      map.set(role.id, role.slots ?? []);
    });
    return map;
  }, [contributor.roles]);

  const renderRoleSecondaryAction = (role: ContributorRoleSummaryDto) => {
    const isAccepted = !!role.acceptedAt;
    const needsAcceptance =
      !role.acceptedAt ||
      (role.slots?.some(
        (slot) => !slot.acceptedAt || (remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot)),
      ) ??
        false);

    if (mode === 'widget' && !isAccepted) {
      const isSelected = selectedRoleIds?.has(role.id) ?? false;
      const hasSelectedSlots = !!(selectedSlotIds?.get(role.id) && selectedSlotIds.get(role.id)!.size > 0);
      return (
        <Checkbox
          color="primary"
          checked={isSelected || hasSelectedSlots}
          onChange={(e) => {
            onRoleSelectionChange?.(role.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (mode === 'details') {
      if (isAccepted && !needsAcceptance) {
        return <CheckCircleOutline color="success" />;
      }
      if (needsAcceptance) {
        return <PendingActionsOutlined color="primary" />;
      }
      return null;
    }

    // Widget mode for already-accepted roles: status icons
    const showMediaIcon = !!role.acceptedAt && roleNeedsMediaUploadAction(role);
    const processingIssue = getRoleMediaProcessingIssue(role);
    const showCheckCircle = !!role.acceptedAt && !needsAcceptance && !showMediaIcon && !processingIssue;

    if (!needsAcceptance && !showMediaIcon && !showCheckCircle && !processingIssue) {
      return null;
    }

    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <MediaProcessingIssueIcon issue={processingIssue} />
        {needsAcceptance && <PendingActionsOutlined color="primary" />}
        {showMediaIcon && <FileUploadOutlined color="primary" />}
        {showCheckCircle && <CheckCircleOutline color="success" />}
      </Stack>
    );
  };

  const renderSlotSecondaryAction = (role: ContributorRoleSummaryDto, slot: ContributorRoleSlotSummaryDto) => {
    const needsRemote = remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot);
    const needsAccept = !slot.acceptedAt;
    const isSelected = !!selectedSlotIds?.get(role.id)?.has(slot.id);

    if (mode === 'details') {
      if (slot.acceptedAt && !needsRemote) {
        return <CheckCircleOutline color="success" />;
      }
      if (needsAccept || needsRemote) {
        return <PendingActionsOutlined color="primary" />;
      }
      return null;
    }

    // Widget: unconfirmed slot → checkbox (confirming also covers remote speakership)
    if (needsAccept) {
      return (
        <Checkbox
          color="primary"
          checked={isSelected}
          onChange={(e) => onSlotSelectionChange?.(role.id, slot.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    // Widget: the accepted slot keeps its checkmark; remote confirmation is rendered separately below.
    if (needsRemote) {
      return (
        <Box sx={{ width: 42, display: 'flex', justifyContent: 'center' }}>
          <CheckCircleOutline color="success" />
        </Box>
      );
    }

    if (slot.acceptedAt) {
      return (
        <Box sx={{ width: 42, display: 'flex', justifyContent: 'center' }}>
          <CheckCircleOutline color="success" />
        </Box>
      );
    }

    return null;
  };

  const renderSlotPrimary = (slot: ContributorRoleSlotSummaryDto, timingText: string) => {
    const showRemoteIcon = remoteSpeakershipAvailable && slot.isRemoteSpeaker === true;

    if (!showRemoteIcon || mode === 'details') {
      return timingText;
    }

    const remoteAccepted = Boolean(slot.remoteSpeakerAcceptedAt);

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" component="span">
        <Typography variant="body2" component="span">
          {timingText}
        </Typography>
        <ClickableIconTooltip
          title={
            remoteAccepted
              ? t('user.contributionsList.remoteSpeakerConfirmedTooltip')
              : t('user.contributionsList.remoteSpeakerPendingTooltip')
          }
          color={remoteAccepted ? 'success.main' : 'primary.main'}
          icon={<PhoneCallbackOutlined sx={{ fontSize: 18 }} />}
        />
      </Stack>
    );
  };

  const renderSlotSecondary = (slot: ContributorRoleSlotSummaryDto) => {
    return getRoleSlotTitle(slot);
  };

  return (
    <Stack sx={{ borderTop: '1px solid', borderColor: 'divider', mx: -1 }}>
      <List disablePadding>
        {contributor.roles.map((role) => {
          const roleSlots = roleSlotsByRoleId.get(role.id) ?? [];
          const timingFallback = t('myEventsList.contributionsList.slotWithoutDescription');
          return (
            <Box key={role.id}>
              <ListItem
                dense
                disablePadding
                secondaryAction={renderRoleSecondaryAction(role)}
                sx={{ boxShadow: 'none', bgcolor: 'background.elevation2', py: 0, minHeight: 'auto' }}
              >
                <ListItemButton
                  onClick={mode === 'widget' ? () => handleOpenRoleModal(role) : undefined}
                  sx={{ 'boxShadow': 'none', 'py': 0.5, 'minHeight': 'auto', '&:hover': { boxShadow: 'none' } }}
                >
                  {mode === 'widget' && roleNeedsAction(role) && (
                    <FiberManualRecord
                      color={getRoleActionDotColor(role)}
                      sx={{ fontSize: 16, mr: 1, flexShrink: 0 }}
                    />
                  )}
                  <ListItemIcon sx={{ minWidth: 32, mr: 0 }}>{getRoleTypeIcon(role.role)}</ListItemIcon>
                  <ListItemText primary={t(`common.roleLabels.${role.role}`)} secondary={getRoleEntityTitle(role)} />
                </ListItemButton>
              </ListItem>
              {roleSlots.length > 0 && (
                <List
                  dense
                  disablePadding
                  sx={{ 'pl': 2, 'pb': 1, '& .MuiListItem-root': { py: 0, minHeight: 'auto' } }}
                >
                  {roleSlots.map((slot) => {
                    const timingText = getRoleSlotTimingText(slot) || timingFallback;
                    const secondary = renderSlotSecondary(slot);
                    const needsRemoteOnly =
                      mode === 'widget' &&
                      !!slot.acceptedAt &&
                      remoteSpeakershipAvailable &&
                      slotNeedsRemoteSpeakerConfirmation(slot);
                    const isRemoteSelected = !!selectedSlotIds?.get(role.id)?.has(slot.id);
                    return (
                      <Box
                        key={slot.id}
                        sx={{
                          'borderRadius': 1,
                          'bgcolor': 'background.elevation2',
                          'overflow': 'hidden',
                          'mb': 0.5,
                          '&:last-child': { mb: 0 },
                        }}
                      >
                        <ListItem
                          onClick={mode === 'widget' ? () => handleOpenRoleModal(role) : undefined}
                          secondaryAction={renderSlotSecondaryAction(role, slot)}
                          sx={{
                            'cursor': mode === 'widget' ? 'pointer' : 'default',
                            'boxShadow': 'none',
                            'py': 0.25,
                            'minHeight': 'auto',
                            '&:hover': {
                              bgcolor: mode === 'widget' ? 'action.hover' : 'background.elevation2',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          <ListItemText
                            primary={renderSlotPrimary(slot, timingText)}
                            secondary={secondary}
                            secondaryTypographyProps={
                              typeof secondary === 'string' || !secondary ? undefined : { component: 'div' }
                            }
                          />
                        </ListItem>
                        {remoteSpeakershipAvailable &&
                          slot.isRemoteSpeaker === true &&
                          !!slot.remoteSpeakerAcceptedAt &&
                          slot.entityType === 'FlowSlot' && (
                            <>
                              <Divider />
                              <ListItem sx={{ py: 0.25, minHeight: 'auto', display: 'block' }}>
                                <JoinLiveButton contributorId={contributor.id} roleSlotId={slot.id} />
                              </ListItem>
                            </>
                          )}
                        {needsRemoteOnly && (
                          <>
                            <Divider />
                            <ListItem
                              secondaryAction={
                                <Checkbox
                                  color="primary"
                                  checked={isRemoteSelected}
                                  onChange={(event) => onSlotSelectionChange?.(role.id, slot.id, event.target.checked)}
                                  onClick={(event) => event.stopPropagation()}
                                />
                              }
                              sx={{ py: 0.25, minHeight: 'auto' }}
                            >
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                      {t('user.contributionsList.speakingRemotely')}
                                    </Typography>
                                    <ClickableIconTooltip
                                      title={t('user.contributionsList.speakingRemotelyTooltip')}
                                      icon={<InfoOutlined sx={{ fontSize: 16 }} />}
                                    />
                                  </Stack>
                                }
                              />
                            </ListItem>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          );
        })}
      </List>
      {mode === 'widget' && (
        <RoleDetailsModal
          open={!!selectedRole}
          onClose={handleCloseRoleModal}
          role={selectedRole}
          contributor={contributor}
          event={event}
          flows={flows}
          sessions={sessions}
          remoteSpeakershipEnabled={remoteSpeakershipAvailable}
        />
      )}
    </Stack>
  );
};

export default ContributionsList;
